/* eslint-disable react/no-array-index-key */
import EndorsementSearch from './endorsement-search';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DeleteConfirmationModal } from 'components/delete-confirmation';
import { format } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import useAlert from 'utils/alert';
import { usePolicyInfoContext } from '../contexts/policyInfoContext';

import { deleteEndorsement } from '../graphql/mutations';
import { mapEndorsements } from '../services';

const useStyles = makeStyles((theme: any) => ({
  tableContainer: { marginTop: theme.spacing(2) },
  table: { backgroundColor: theme.palette.background.default },
  cellHeader: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottomColor: theme.palette.grey[700],
  },
  cell: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottomColor: theme.palette.grey[700],
  },
  text: {
    color: 'black',
    fontSize: '0.875rem',
  },
  title: {
    color: 'black',
    fontSize: '1.2rem',
  },
  icon: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontSize: '0.875rem',
    '&:hover': { color: theme.palette.common.black },
  },
  container: {
    minHeight: '7rem',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
  },
}));

type ModalData =
	| {
		modal: 'removeEndorsement';
		endorsementCode: string;
	}
	| {
		modal: '';
	};

const TableEndorsements = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { accounting, setLatestUpdate, selectedTab, endorsements, setEndorsements, options } =
		usePolicyInfoContext();
  const { id } = useParams<any>();
  const showAlert = useAlert();

  const [modalData, setModalData] = useState<ModalData>();

  const selectedPolicy: 'owners_policy_tab' | 'lenders_policy_tab' = selectedTab?.id!;
  const currentEndorsements = useMemo(() => {
    return mapEndorsements(
      selectedPolicy === 'owners_policy_tab'
        ? endorsements.ownersEndorsements
        : endorsements.lendersEndorsements,
      options.endorsements
    );
  }, [endorsements, selectedPolicy, options]);

  const onCloseModal = useCallback(() => setModalData({ modal: '' }), []);
  const { mutate: removeEndorsemet, ...removeEndorsementeResponse } =
		useMutation(deleteEndorsement);

  const onRemoveEndorsement = useCallback(() => {
    if (!modalData || modalData.modal !== 'removeEndorsement') return;

    const kind = selectedPolicy === 'lenders_policy_tab' ? 'lenders' : 'owners';

    removeEndorsemet(
      {
        id,
        kind,
        code: modalData.endorsementCode,
      },
      {
        onSuccess: (data) => {
          removeEndorsementeResponse.reset();
          showAlert(t('policyInfoAndRates:endorsementRemoved'), 'success');
          setEndorsements({
            lendersEndorsements:
							data.deleteOrderPolicyEndorsement.lenders_policy.endorsements ?? [],
            ownersEndorsements: data.deleteOrderPolicyEndorsement.owners_policy.endorsements ?? [],
          });

          const now = format(new Date(), 'hh:mm');
          setLatestUpdate({ time: now, type: 'success', message: `updated at  ${now}` });
          onCloseModal();
        },
      }
    );
  }, [removeEndorsemet, selectedPolicy, id, modalData, removeEndorsementeResponse, setEndorsements, setLatestUpdate, showAlert, t, onCloseModal]);

  return (
    <Box className={classes.container}>
      <Box
        className={classes.headerContainer}
        style={{ borderBottom: '1px solid lightgray', padding: '8px 20px' }}
      >
        <Typography className={classes.title}>{t('policyInfoAndRates:endorsements')}</Typography>

        <EndorsementSearch />
      </Box>

      <Box
        style={{
				  overflowY: 'scroll',
				  overflowX: 'hidden',
				  scrollbarWidth: 'thin',
				  display: 'flex',
				  flexDirection: 'column',
				  maxHeight: '300px',
				  padding: '10px',
				  gap: '10px',
        }}
      >
        {currentEndorsements.map((endorsement) => (
          <Box
            key={endorsement.code}
            style={{
						  display: 'flex',
						  justifyContent: 'space-between',
						  alignItems: 'center',
						  backgroundColor: '#d3d3d345',
						  borderRadius: '5px',
						  padding: '5px 10px',
            }}
          >
            <Tooltip title={endorsement.optionsDescription}>
              <Typography className={classes.text}>{endorsement.description}</Typography>
            </Tooltip>
            <IconButton
              className={classes.icon}
              onClick={() => {
							  setModalData({
							    modal: 'removeEndorsement',
							    endorsementCode: endorsement.code,
							  });
              }}
              size="large"
            >
              <FontAwesomeIcon icon="times" />
            </IconButton>
          </Box>
        ))}
      </Box>
      <DeleteConfirmationModal
        title={t('policyInfoAndRates:removeEndorsement')}
        confirmationMessage={t('policyInfoAndRates:removeConfirmation')}
        isOpen={modalData?.modal === 'removeEndorsement'}
        isRemoveLoading={removeEndorsementeResponse.isLoading}
        onConfirm={onRemoveEndorsement}
        onClose={onCloseModal}
        buttonConfirmText={t('policyInfoAndRates:removeButton')}
      />
    </Box>
  );
};

export default TableEndorsements;
